/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-runloop, ember/require-tagless-components */
import {
  attributeBindings,
  classNames,
  tagName,
} from '@ember-decorators/component';
import Component from '@ember/component';
import { scheduleOnce } from '@ember/runloop';
import { isPresent } from '@ember/utils';

// D3 imports
import { select } from 'd3-selection';
import { scaleLinear, scaleOrdinal, scalePoint } from 'd3-scale';
import { min as d3Min, max as d3Max, quantile } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import { schemeCategory10 } from 'd3-scale-chromatic';

@tagName('svg')
@classNames('chart', 'chart-box')
@attributeBindings('width', 'height')
export default class ChartBox extends Component {
  width = 480;
  height = 320;
  barWidth = 30;
  margin = Object.freeze({ top: 20, right: 20, bottom: 20, left: 20 });
  ticks = 10;
  data = [];

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Schedule a call to our `drawCircles` method on Ember's "render" queue, which will
    // happen after the component has been placed in the DOM, and subsequently
    // each time data is changed.
    scheduleOnce('render', this, this.draw);
  }

  draw() {
    let plot = select(this.element);
    let data = this.data;
    let margin = this.margin;
    let barWidth = this.barWidth;
    let width = this.width - margin.left - margin.right;
    let height = this.height - margin.top - margin.bottom;
    let keys = [];
    let min = this.min;
    let max = this.max;
    let needsBounds = !isPresent(min) || !isPresent(max);

    // Don't try to render if we have no data
    if (!data.length) {
      return;
    }

    // Sort group counts so quantile methods work
    data.forEach(function (groupCount, key) {
      if (groupCount.length) {
        data[key] = groupCount.sort(this.sortNumber);
        keys.push(key);
      }
    }, this);

    // Setup a color scale for filling each box
    let colorScale = scaleOrdinal(schemeCategory10).domain(keys);

    // Prepare the data for the box plots
    let boxPlotData = [];

    // Initialise min/max if not specified
    if (needsBounds) {
      min = Infinity;
      max = -Infinity;
    }
    data.forEach(function (groupCount, key) {
      let record = {};
      let localMin = d3Min(groupCount);
      let localMax = d3Max(groupCount);

      // If we're calculating min/max, do so
      if (needsBounds) {
        min = d3Min([min, localMin]);
        max = d3Max([max, localMax]);
      }

      record['key'] = key;
      record['counts'] = groupCount;
      record['quartile'] = this.boxQuartiles(groupCount);
      record['whiskers'] = [localMin, localMax];
      record['color'] = colorScale(key);

      boxPlotData.push(record);
    }, this);

    // Adjust calculated bounds if min === max, or the plot won't look right
    if (needsBounds && min === max) {
      min = 0;
      max = 2 * max;
    }

    // Compute an ordinal xScale for the keys in boxPlotData
    let xScale = scalePoint()
      .domain(keys)
      .rangeRound([0, width])
      .padding([0.5]);

    // Compute a global y scale based on the global counts
    let yScale = scaleLinear().domain([max, min]).range([0, height]);

    // Remove previous chart (if there is one), then append the new root element.
    plot.selectAll('*').remove();
    let outerG = plot
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // Move the left axis over 25 pixels, and the bottom axis over 35 pixels
    let axisG = outerG.append('g').attr('transform', 'translate(25,0)');
    let axisBottomG = outerG
      .append('g')
      .attr('transform', 'translate(35,' + height + ')');

    // Setup the group the box plot elements will render in
    let g = outerG.append('g').attr('transform', 'translate(20,0)');

    // Draw the box plot vertical lines
    g.selectAll('.verticalLines')
      .data(boxPlotData)
      .enter()
      .append('line')
      .attr('x1', function (datum) {
        return xScale(datum.key) + barWidth / 2;
      })
      .attr('y1', function (datum) {
        let whisker = datum.whiskers[0];
        return yScale(whisker);
      })
      .attr('x2', function (datum) {
        return xScale(datum.key) + barWidth / 2;
      })
      .attr('y2', function (datum) {
        let whisker = datum.whiskers[1];
        return yScale(whisker);
      })
      .attr('stroke', '#000')
      .attr('stroke-width', 1)
      .attr('fill', 'none');

    // Draw the boxes of the box plot, filled in white and on top of vertical lines
    g.selectAll('rect')
      .data(boxPlotData)
      .enter()
      .append('rect')
      .attr('width', barWidth)
      .attr('height', function (datum) {
        let quartiles = datum.quartile;
        let height = yScale(quartiles[0]) - yScale(quartiles[2]);
        return height;
      })
      .attr('x', function (datum) {
        return xScale(datum.key);
      })
      .attr('y', function (datum) {
        return yScale(datum.quartile[2]);
      })
      .attr('fill', function (datum) {
        return datum.color;
      })
      .attr('stroke', '#000')
      .attr('stroke-width', 1);

    // Now render all the horizontal lines at once - the whiskers and the median
    var horizontalLineConfigs = [
      // Top whisker
      {
        x1: function (datum) {
          return xScale(datum.key);
        },
        y1: function (datum) {
          return yScale(datum.whiskers[0]);
        },
        x2: function (datum) {
          return xScale(datum.key) + barWidth;
        },
        y2: function (datum) {
          return yScale(datum.whiskers[0]);
        },
      },
      // Median line
      {
        x1: function (datum) {
          return xScale(datum.key);
        },
        y1: function (datum) {
          return yScale(datum.quartile[1]);
        },
        x2: function (datum) {
          return xScale(datum.key) + barWidth;
        },
        y2: function (datum) {
          return yScale(datum.quartile[1]);
        },
      },
      // Bottom whisker
      {
        x1: function (datum) {
          return xScale(datum.key);
        },
        y1: function (datum) {
          return yScale(datum.whiskers[1]);
        },
        x2: function (datum) {
          return xScale(datum.key) + barWidth;
        },
        y2: function (datum) {
          return yScale(datum.whiskers[1]);
        },
      },
    ];

    for (let i = 0; i < horizontalLineConfigs.length; i++) {
      let lineConfig = horizontalLineConfigs[i];

      // Draw the whiskers at the min for this series
      g.selectAll('.whiskers')
        .data(boxPlotData)
        .enter()
        .append('line')
        .attr('x1', lineConfig.x1)
        .attr('y1', lineConfig.y1)
        .attr('x2', lineConfig.x2)
        .attr('y2', lineConfig.y2)
        .attr('stroke', '#000')
        .attr('stroke-width', 1)
        .attr('fill', 'none');
    }

    // Setup a scale on the left
    let leftAxis = axisLeft(yScale).ticks(this.ticks);
    axisG.append('g').attr('class', 'axis axis--y').call(leftAxis);

    // Setup a series axis on the bottom, if we have more than one box
    if (data.length > 1) {
      let bottomAxis = axisBottom(xScale);
      axisBottomG.append('g').attr('class', 'axis axis--x').call(bottomAxis);
    }
  }

  boxQuartiles(d) {
    return [quantile(d, 0.25), quantile(d, 0.5), quantile(d, 0.75)];
  }

  // Perform a numeric sort on an array
  sortNumber(a, b) {
    return a - b;
  }
}
